import * as Sentry from '@sentry/react';
import * as SentryCore from '@sentry/core';
import { useCallback, useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useIdentity } from './providers/IdentityProvider';

Sentry.init({
  debug: false,
  dsn: 'https://b890e8b52b274a48aeff40a10177b5be@o1409683.ingest.sentry.io/4504208406872064',
  tunnel: 'https://sentry.allbin.se',
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Off on every `vite` dev server (start/msw/gallery) and in the deployed dev
  // environment; on in test, canary and production builds.
  enabled: !import.meta.env.DEV && __SENTRY_ENVIRONMENT__ !== 'development',
  environment: __SENTRY_ENVIRONMENT__,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.01,
});

const heartbeatInterval = 1 * 60 * 1000; // 1 minute

function heartbeat(id: string): void {
  Sentry.addBreadcrumb({
    message: 'Heartbeat',
    category: 'heartbeat',
    level: 'info',
  });

  SentryCore.captureCheckIn({
    monitorSlug: 'heartbeats',
    status: 'ok',
  });
  console.log('heartbeat', id);
}

export const useSentry = (): void => {
  const { id } = useIdentity(useCallback(({ id }) => ({ id }), []));
  useEffect(() => {
    if (!id || import.meta.env.DEV) return;

    Sentry.setUser({ id });

    console.log('registering heartbeat interval', id);
    heartbeat(id);
    const timeout = setInterval(() => heartbeat(id), heartbeatInterval);

    return () => {
      clearInterval(timeout);
      console.log('unregistering heartbeat interval', id);
    };
  }, [id]);
};
